import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { StepUpDialog } from "../components/StepUpDialog";
import { SsoStatusCard } from "../components/sso/SsoStatusCard";
import { CertificateRotationCard } from "../components/sso/CertificateRotationCard";
import { BreakglassCard } from "../components/sso/BreakglassCard";
import { EmergencyIdentitiesCard } from "../components/sso/EmergencyIdentitiesCard";
import { DrillCard } from "../components/sso/DrillCard";
import { RotationTemplatesCard } from "../components/sso/RotationTemplatesCard";
import { SsoDenialsCard } from "../components/sso/SsoDenialsCard";
import { SsoReturnAddresses } from "../components/sso/SsoReturnAddresses";
import { formatWhen } from "../components/sso/ssoFormat";

function AccessHistory({ events }) {
  return (
    <section className="border p-6" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="breakglass-history">
      <div className="eyebrow mb-3">Emergency access history</div>
      <div className="max-h-[420px] overflow-auto">
        {events.map((event) => (
          <div key={`${event.occurred_at}-${event.action}-${event.summary}`} className="py-2.5 border-b text-sm" style={{ borderColor: "var(--border)" }} data-testid="breakglass-event">
            <span className={`pill text-[9px] mr-2 ${event.outcome === "success" ? "" : "pill-danger"}`}>{event.action.replace("breakglass.", "").replace(/_/g, " ")}</span>
            <span style={{ color: "var(--ink)" }}>{event.summary}</span>
            <span className="block text-xs mt-1" style={{ color: "var(--muted)" }}>{formatWhen(event.occurred_at)} · {event.actor_name} · {event.source_ip || "—"}</span>
          </div>
        ))}
        {!events.length && <p className="text-sm" style={{ color: "var(--muted)" }} data-testid="breakglass-history-empty">No emergency access activity recorded.</p>}
      </div>
    </section>
  );
}

export default function AdminSSO() {
  const { user } = useAuth();
  const allowed = (user?.permissions || []).includes("system");
  if (!allowed) {
    return (
      <div className="mx-auto max-w-[1400px] px-6 py-16 lg:px-10 text-sm" style={{ color: "var(--muted)" }} data-testid="admin-sso-forbidden">
        Only system administrators can view SSO &amp; Emergency Access.
      </div>
    );
  }
  return <SsoWorkspace selfId={user?.id} canManageUsers={(user?.permissions || []).includes("users")} />;
}

function SsoWorkspace({ selfId, canManageUsers }) {
  const [sso, setSso] = useState(null);
  const [breakglass, setBreakglass] = useState(null);
  const [stepUp, setStepUp] = useState(null);
  const [templates, setTemplates] = useState([]);

  const load = useCallback(() => {
    const fail = (e) => toast.error(formatApiError(e.response?.data?.detail));
    api.get("/admin/sso/overview").then(({ data }) => setSso(data)).catch(fail);
    api.get("/admin/breakglass").then(({ data }) => setBreakglass(data)).catch(fail);
    api.get("/admin/sso/templates").then(({ data }) => setTemplates(data)).catch(fail);
  }, []);
  const closeStepUp = useCallback(() => setStepUp(null), []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-10 lg:px-10" data-testid="admin-sso">
      <div className="eyebrow mb-2">System</div>
      <h1 className="font-heading font-black text-4xl md:text-5xl leading-none tracking-tight mb-3" style={{ color: "var(--ink)" }}>
        SSO &amp; Emergency Access
      </h1>
      <p className="text-sm max-w-2xl mb-10" style={{ color: "var(--muted)" }}>
        Microsoft Entra metadata, certificate rotation and the breakglass path for an Entra outage.
        Only public certificate details are handled here; private keys and secrets stay in secure
        server settings. Every change is recorded.
      </p>
      <div className="grid gap-8">
        {sso && <SsoStatusCard credential={sso.credential} lastTest={sso.last_test} warnDays={sso.limits.warn_days} />}
        {sso?.return_addresses && <SsoReturnAddresses data={sso.return_addresses} />}
        {canManageUsers && <SsoDenialsCard selfId={selfId} />}
        {breakglass && (
          <div className="grid gap-8 lg:grid-cols-2">
            <BreakglassCard data={breakglass} onStepUp={setStepUp} onChanged={load} />
            <EmergencyIdentitiesCard data={breakglass} onStepUp={setStepUp} onChanged={load} />
          </div>
        )}
        {breakglass && <DrillCard breakglass={breakglass} onStepUp={setStepUp} />}
        {sso && <CertificateRotationCard records={sso.records} onStepUp={setStepUp} onChanged={load} readOnly={breakglass?.viewer_is_emergency} validHours={sso.limits.verify_valid_hours} templates={templates} />}
        {sso && <RotationTemplatesCard templates={templates} onChanged={load} readOnly={breakglass?.viewer_is_emergency} />}
        {breakglass && <AccessHistory events={breakglass.events} />}
      </div>
      <StepUpDialog request={stepUp} onClose={closeStepUp} />
    </div>
  );
}
